"use client";

import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * UnifiedThemeProvider ensures consistent theme application across all pages
 * by synchronizing CSS classes and variables with next-themes
 */
export default function UnifiedThemeProvider({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Apply consistent theme classes to document element
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const htmlElement = document.documentElement;

    // Remove all theme-specific classes
    htmlElement.classList.remove(
      'planning-dark',
      'planning-light',
      'planning-vintage',
      'theme-dark',
      'theme-light',
      'theme-system'
    );

    // Apply the correct theme class based on resolved theme
    if (resolvedTheme === 'light') {
      htmlElement.classList.add('planning-light');
    } else if (resolvedTheme === 'dark') {
      htmlElement.classList.add('planning-dark');
    }

    // For newspaper/vintage view, check if we're on planning-updates with newspaper mode
    const view = searchParams?.get?.('view');
    const themeParam = searchParams?.get?.('theme');
    const isVintageMode = view === 'newspaper' || themeParam === 'vintage';
    if (isVintageMode) {
      htmlElement.classList.add('planning-vintage');
    }

  }, [resolvedTheme, pathname, searchParams]);

  // Sync CSS custom properties
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const root = document.documentElement;
    
    // Ensure CSS variables are properly set based on theme
    if (resolvedTheme === 'light') {
      // Light mode civic noir variables: cream canvas, parchment surfaces.
      root.style.setProperty('--c-bg', '#F5EBD8');
      root.style.setProperty('--c-bg-rgb', '245, 235, 216');
      root.style.setProperty('--c-bg-secondary', '#EFE0C8');
      root.style.setProperty('--c-surface', 'rgba(255, 248, 235, 0.96)');
      root.style.setProperty('--c-surface-rgb', '255, 248, 235');
      root.style.setProperty('--c-border', '#D9C4A3');
      root.style.setProperty('--c-border-rgb', '217, 196, 163');
      root.style.setProperty('--c-surface-hover', '#F1E2CB');
      root.style.setProperty('--svg-opacity', '0');
      root.style.setProperty('--c-text-primary', '#342519');
      root.style.setProperty('--c-text-secondary', '#654E39');
      root.style.setProperty('--c-text-tertiary', '#8A7157');
      root.style.setProperty('--c-text-disabled', '#9D8871');
      root.style.setProperty('--c-accent', '#D3AF37');
      root.style.setProperty('--c-accent-rgb', '211, 175, 55');
      root.style.setProperty('--c-accent-muted', 'rgba(211, 175, 55, 0.15)');
      root.style.setProperty('--c-error', '#B45A2A');
      
      // Update Tailwind CSS variables for light mode
      root.style.setProperty('--background', '38 48% 92%');
      root.style.setProperty('--foreground', '29 34% 14%');
      root.style.setProperty('--card', '43 70% 96%');
      root.style.setProperty('--card-foreground', '29 34% 14%');
      
    } else if (resolvedTheme === 'dark') {
      // Dark mode civic noir variables
      root.style.setProperty('--c-bg', '#0D0F13');
      root.style.setProperty('--c-bg-rgb', '13, 15, 19');
      root.style.setProperty('--c-bg-secondary', '#181C22');
      root.style.setProperty('--c-surface', '#181C22');
      root.style.setProperty('--c-surface-rgb', '24, 28, 34');
      root.style.setProperty('--c-border', '#20242C');
      root.style.setProperty('--c-border-rgb', '32, 36, 44');
      root.style.setProperty('--svg-opacity', '1');
      root.style.setProperty('--c-text-primary', '#ECEFF1');
      root.style.setProperty('--c-text-secondary', '#B0B6C3');
      root.style.setProperty('--c-text-disabled', '#6B7280');
      root.style.setProperty('--c-accent', '#D3AF37');
      root.style.setProperty('--c-accent-muted', 'rgba(211, 175, 55, 0.15)');
      root.style.setProperty('--c-surface-hover', '#1B222B');
      root.style.setProperty('--c-error', '#FF4D4F');
      
      // Update Tailwind CSS variables for dark mode
      root.style.setProperty('--background', '222.2 84% 4.9%');
      root.style.setProperty('--foreground', '210 40% 98%');
      root.style.setProperty('--card', '222.2 84% 4.9%');
      root.style.setProperty('--card-foreground', '210 40% 98%');
    }
  }, [resolvedTheme]);

  return <>{children}</>;
} 
